import { ConfirmationDialog } from '@/components/core/ConfirmationDialog'
import { EditableField } from '@/components/core/EditableField'
import { Button } from '@/components/ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { Tool } from '@/lib/api/client'
import { SkeletonTableRows } from '@/pages/ProjectsPage/components/SkeletonTableRows'
import { TrashIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'

const headerColumns = ['Tool', '']

export const OrgToolsTable = ({
  tools,
  totalTools,
  isLoading,
  fetchNextPage,
  onDelete,
  onUpdate,
  isReadOnly = false,
}: {
  tools: Tool[]
  totalTools: number
  isLoading: boolean
  fetchNextPage: () => void
  onDelete: (params: { toolId: string }) => Promise<unknown> | void
  onUpdate: (params: {
    toolId: string
    name: string
  }) => Promise<unknown> | void
  isReadOnly?: boolean
}) => {
  const visibleHeaderColumns = isReadOnly ? ['Tool'] : headerColumns

  if (isLoading) {
    return <SkeletonTableRows />
  }

  return (
    <div
      className="rounded-xl border border-zinc-300 bg-white overflow-hidden"
      data-testid="org-tools-table"
    >
      <table className="min-w-full">
        <thead className="text-left text-sm font-normal text-zinc-500 bg-zinc-100">
          <tr>
            {visibleHeaderColumns.map((header, index) => (
              <th key={index} scope="col" className="px-6 py-3">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tools.map((tool) => (
            <tr
              className="whitespace-nowrap text-sm text-black border-t border-zinc-200"
              key={tool.id}
              data-testid={`org-tools-row-${tool.id}`}
            >
              <td className="px-6 py-3 align-middle">
                <ToolNameField
                  tool={tool}
                  onRename={onUpdate}
                  isReadOnly={isReadOnly}
                />
              </td>
              {!isReadOnly && (
                <td className="px-6 py-3 align-middle">
                  <DeleteButton tool={tool} onDelete={onDelete} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {totalTools !== tools.length && (
        <div className="flex justify-center border-t border-zinc-200 bg-zinc-50">
          <Button
            onClick={fetchNextPage}
            className="my-4 px-10 rounded-full"
            variant="outline"
            data-testid="org-tools-view-more-button"
          >
            View More
          </Button>
        </div>
      )}
    </div>
  )
}

function ToolNameField({
  tool,
  onRename,
  isReadOnly = false,
}: {
  tool: Tool
  onRename: (params: {
    toolId: string
    name: string
  }) => Promise<unknown> | void
  isReadOnly?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [name, setName] = useState(tool.name)

  useEffect(() => {
    setName(tool.name)
  }, [tool.name])

  const handleRenameTool = async () => {
    await onRename({ toolId: tool.id, name })
    setShowConfirmationModal(false)
  }

  if (isReadOnly) {
    return (
      <span className="text-ellipsis overflow-hidden whitespace-nowrap block leading-tight text-base">
        {name}
      </span>
    )
  }

  return (
    <>
      <ConfirmationDialog
        title={`Rename ${tool.name} to ${name}?`}
        isOpen={showConfirmationModal}
        onConfirm={handleRenameTool}
        closeDialog={() => {
          setName(tool.name)
          setShowConfirmationModal(false)
        }}
      >
        <span>
          Any unpublished documents that reference this tool will be updated.
        </span>
      </ConfirmationDialog>
      <ContextMenu>
        <ContextMenuTrigger>
          <EditableField
            value={name}
            shouldStopPropagation={true}
            inputClassNames="h-7 w-full rounded-md border-zinc-300 bg-white px-2 text-sm leading-tight"
            textClassNames="leading-tight font-medium"
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSaveHandler={(newName) => {
              if (newName === tool.name) return
              setName(newName)
              setShowConfirmationModal(true)
            }}
          />
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem onMouseDown={() => setIsEditing(true)}>
            Rename
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </>
  )
}

function DeleteButton({
  tool,
  onDelete,
}: {
  tool: Tool
  onDelete: (params: { toolId: string }) => Promise<unknown> | void
}) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const handleDeleteTool = async () => {
    await onDelete({ toolId: tool.id })
    setShowConfirmationModal(false)
  }

  return (
    <>
      <ConfirmationDialog
        title={`Delete ${tool.name}?`}
        isOpen={showConfirmationModal}
        onConfirm={handleDeleteTool}
        closeDialog={() => setShowConfirmationModal(false)}
      >
        <span>
          Any unpublished documents that reference this tool will be updated.
        </span>
      </ConfirmationDialog>
      <button
        onClick={() => setShowConfirmationModal(true)}
        data-testid={`org-tools-delete-button-${tool.id}`}
      >
        <TrashIcon className="h-4 w-4 text-zinc-400 hover:text-zinc-600" />
      </button>
    </>
  )
}
